import type {
  GraphChangeSetApplyResponse,
  GraphChangeSetAuditExportResponse,
  GraphChangeSetCreateResponse,
  GraphChangeSetAuditLogListResponse,
  GraphChangeSetListResponse,
  GraphChangeSetRevertResponse,
  GraphCsvImportResponse,
  GraphFrameCommitResponse,
  GraphVisualizationFrame
} from "@salesko/contracts";
import { useMutation } from "@tanstack/react-query";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import {
  applyGraphChangeSet,
  createGraphChangeSet,
  exportGraphChangeSetAudit,
  importCsvGraphChangeSet,
  listGraphChangeSetAuditLogs,
  listGraphChangeSets,
  revertGraphChangeSet
} from "../../lib/api";
import type { Dictionary } from "../../i18n";
import { downloadJsonArtifact } from "../lib/download";
import type {
  ChangeSetActionVariables,
  ChangeSetAuditExportState,
  ChangeSetAuditLogState,
  ChangeSetHistoryState,
  ChangeSetLifecycleState,
  CreateChangeSetVariables,
  CsvImportVariables
} from "../types";

type UseGraphChangeSetsOptions = {
  copy: Dictionary["changeSet"];
  frameRecordId: string | null;
  onFrameMutated: (frame: GraphVisualizationFrame, commit: GraphFrameCommitResponse) => void;
  onInvalidateFrames: () => void;
};

export function useGraphChangeSets({
  copy,
  frameRecordId,
  onFrameMutated,
  onInvalidateFrames
}: UseGraphChangeSetsOptions) {
  const csvFileInputRef = useRef<HTMLInputElement | null>(null);
  const [changeSetState, setChangeSetState] = useState<ChangeSetLifecycleState>({ status: "idle" });
  const [changeSetHistoryState, setChangeSetHistoryState] = useState<ChangeSetHistoryState>({ status: "idle" });
  const [changeSetAuditLogState, setChangeSetAuditLogState] = useState<ChangeSetAuditLogState>({ status: "idle" });
  const [changeSetAuditExportState, setChangeSetAuditExportState] = useState<ChangeSetAuditExportState>({
    status: "idle"
  });

  const changeSetHistoryMutation = useMutation<GraphChangeSetListResponse, Error, { frameRecordId: string }>({
    mutationFn: ({ frameRecordId: historyFrameRecordId }) => listGraphChangeSets(historyFrameRecordId, 10),
    onMutate: ({ frameRecordId: historyFrameRecordId }) => {
      setChangeSetHistoryState({
        status: "loading",
        frameRecordId: historyFrameRecordId
      });
    },
    onSuccess: (response, variables) => {
      setChangeSetHistoryState({
        status: "success",
        frameRecordId: variables.frameRecordId,
        response
      });
    },
    onError: (error, variables) => {
      setChangeSetHistoryState({
        status: "error",
        frameRecordId: variables.frameRecordId,
        message: error.message || copy.errors.historyFailed
      });
    }
  });

  const changeSetAuditLogMutation = useMutation<
    GraphChangeSetAuditLogListResponse,
    Error,
    { changeSetId: string; frameRecordId: string }
  >({
    mutationFn: ({ changeSetId, frameRecordId: auditFrameRecordId }) =>
      listGraphChangeSetAuditLogs(auditFrameRecordId, changeSetId, 10),
    onMutate: ({ changeSetId, frameRecordId: auditFrameRecordId }) => {
      setChangeSetAuditLogState({
        status: "loading",
        changeSetId,
        frameRecordId: auditFrameRecordId
      });
    },
    onSuccess: (response, variables) => {
      setChangeSetAuditLogState({
        status: "success",
        changeSetId: variables.changeSetId,
        frameRecordId: variables.frameRecordId,
        response
      });
    },
    onError: (error, variables) => {
      setChangeSetAuditLogState({
        status: "error",
        changeSetId: variables.changeSetId,
        frameRecordId: variables.frameRecordId,
        message: error.message || copy.errors.auditLogFailed
      });
    }
  });

  const changeSetAuditExportMutation = useMutation<
    GraphChangeSetAuditExportResponse,
    Error,
    { changeSetId: string; frameRecordId: string }
  >({
    mutationFn: ({ changeSetId, frameRecordId: auditFrameRecordId }) =>
      exportGraphChangeSetAudit(auditFrameRecordId, changeSetId),
    onMutate: ({ changeSetId, frameRecordId: auditFrameRecordId }) => {
      setChangeSetAuditExportState({
        status: "loading",
        changeSetId,
        frameRecordId: auditFrameRecordId
      });
    },
    onSuccess: (response, variables) => {
      downloadJsonArtifact(response.filename, response.artifact);
      setChangeSetAuditExportState({
        status: "success",
        changeSetId: variables.changeSetId,
        filename: response.filename,
        frameRecordId: variables.frameRecordId
      });
    },
    onError: (error, variables) => {
      setChangeSetAuditExportState({
        status: "error",
        changeSetId: variables.changeSetId,
        frameRecordId: variables.frameRecordId,
        message: error.message || copy.errors.auditExportFailed
      });
    }
  });

  const csvImportMutation = useMutation<GraphCsvImportResponse, Error, CsvImportVariables>({
    mutationFn: ({ csv, fileName, frameRecordId: importFrameRecordId }) =>
      importCsvGraphChangeSet(importFrameRecordId, {
        csv,
        clientMutationId: csvImportClientMutationId(fileName),
        reason: csvImportReason(fileName)
      }),
    onMutate: ({ fileName }) => {
      setChangeSetState({
        status: "importing",
        fileName
      });
    },
    onSuccess: (response, variables) => {
      setChangeSetState({
        status: "pending",
        importSummary: response.importSummary,
        response,
        sourceLabel: variables.fileName
      });
      changeSetHistoryMutation.mutate({ frameRecordId: variables.frameRecordId });
      if (changeSetAuditLogState.status !== "idle" && changeSetAuditLogState.changeSetId === response.changeSet.id) {
        changeSetAuditLogMutation.mutate({
          changeSetId: response.changeSet.id,
          frameRecordId: variables.frameRecordId
        });
      }
    },
    onError: (error, variables) => {
      setChangeSetState({
        status: "error",
        fileName: variables.fileName,
        message: error.message || copy.errors.csvImportFailed
      });
    }
  });

  const createChangeSetMutation = useMutation<
    Awaited<ReturnType<typeof createGraphChangeSet>>,
    Error,
    CreateChangeSetVariables
  >({
    mutationFn: ({ frameRecordId: targetFrameRecordId, input }) => createGraphChangeSet(targetFrameRecordId, input),
    onMutate: ({ sourceLabel }) => {
      setChangeSetState({
        status: "creating",
        sourceLabel
      });
    },
    onSuccess: (response, variables) => {
      setChangeSetState({
        status: "pending",
        response,
        sourceLabel: variables.sourceLabel
      });
      changeSetHistoryMutation.mutate({ frameRecordId: variables.frameRecordId });
      if (changeSetAuditLogState.status !== "idle" && changeSetAuditLogState.changeSetId === response.changeSet.id) {
        changeSetAuditLogMutation.mutate({
          changeSetId: response.changeSet.id,
          frameRecordId: variables.frameRecordId
        });
      }
    },
    onError: (error, variables) => {
      setChangeSetState({
        status: "error",
        fileName: variables.sourceLabel,
        message: error.message || copy.errors.createFailed
      });
    }
  });

  const applyChangeSetMutation = useMutation<GraphChangeSetApplyResponse, Error, ChangeSetActionVariables>({
    mutationFn: ({ changeSetId }) => applyGraphChangeSet(changeSetId),
    onMutate: ({ changeSetId, sourceLabel, importSummary }) => {
      setChangeSetState({
        status: "applying",
        changeSetId,
        sourceLabel,
        ...(importSummary ? { importSummary } : {})
      });
    },
    onSuccess: (response, variables) => {
      onFrameMutated(response.frame.frame, commitFromChangeSetResponse(response));
      onInvalidateFrames();
      setChangeSetState({
        status: "applied",
        sourceLabel: variables.sourceLabel,
        ...(variables.importSummary ? { importSummary: variables.importSummary } : {}),
        response
      });
      changeSetHistoryMutation.mutate({ frameRecordId: variables.frameRecordId });
      if (changeSetAuditLogState.status !== "idle" && changeSetAuditLogState.changeSetId === response.changeSet.id) {
        changeSetAuditLogMutation.mutate({
          changeSetId: response.changeSet.id,
          frameRecordId: variables.frameRecordId
        });
      }
    },
    onError: (error, variables) => {
      setChangeSetState({
        status: "error",
        fileName: variables.sourceLabel,
        message: error.message || copy.errors.applyFailed
      });
    }
  });

  const revertChangeSetMutation = useMutation<GraphChangeSetRevertResponse, Error, ChangeSetActionVariables>({
    mutationFn: ({ changeSetId }) => revertGraphChangeSet(changeSetId),
    onMutate: ({ changeSetId, sourceLabel, importSummary }) => {
      setChangeSetState({
        status: "reverting",
        changeSetId,
        sourceLabel,
        ...(importSummary ? { importSummary } : {})
      });
    },
    onSuccess: (response, variables) => {
      onFrameMutated(response.frame.frame, commitFromChangeSetResponse(response));
      onInvalidateFrames();
      setChangeSetState({
        status: "reverted",
        sourceLabel: variables.sourceLabel,
        ...(variables.importSummary ? { importSummary: variables.importSummary } : {}),
        response
      });
      changeSetHistoryMutation.mutate({ frameRecordId: variables.frameRecordId });
      if (changeSetAuditLogState.status !== "idle" && changeSetAuditLogState.changeSetId === response.changeSet.id) {
        changeSetAuditLogMutation.mutate({
          changeSetId: response.changeSet.id,
          frameRecordId: variables.frameRecordId
        });
      }
    },
    onError: (error, variables) => {
      setChangeSetState({
        status: "error",
        fileName: variables.sourceLabel,
        message: error.message || copy.errors.revertFailed
      });
    }
  });

  useEffect(() => {
    if (!frameRecordId) {
      setChangeSetHistoryState({ status: "idle" });
      return;
    }

    changeSetHistoryMutation.mutate({ frameRecordId });
  }, [frameRecordId]);

  const resetChangeSetLifecycle = () => {
    setChangeSetState({ status: "idle" });
  };

  useEffect(() => {
    if (!shouldAutoClearChangeSetState(changeSetState.status)) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setChangeSetState({ status: "idle" });
    }, StatusAutoClearMs);
    return () => window.clearTimeout(timeout);
  }, [changeSetState]);

  const resetChangeSetContext = () => {
    setChangeSetState({ status: "idle" });
    setChangeSetAuditLogState({ status: "idle" });
    setChangeSetAuditExportState({ status: "idle" });
  };

  const handleCsvImportClick = () => {
    if (!frameRecordId) {
      setChangeSetState({
        status: "error",
        message: copy.errors.csvNeedsCommittedFrame
      });
      return;
    }

    csvFileInputRef.current?.click();
  };

  const handleCsvFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) {
      return;
    }

    if (!frameRecordId) {
      setChangeSetState({
        status: "error",
        fileName: file.name,
        message: copy.errors.csvNeedsCommittedFrame
      });
      return;
    }

    if (file.size > 100_000) {
      setChangeSetState({
        status: "error",
        fileName: file.name,
        message: copy.errors.csvTooLarge
      });
      return;
    }

    const csv = await file.text();
    if (csv.trim().length === 0) {
      setChangeSetState({
        status: "error",
        fileName: file.name,
        message: copy.errors.csvEmpty
      });
      return;
    }

    if (!confirmCsvImport(file.name, copy)) {
      return;
    }

    csvImportMutation.mutate({
      csv,
      fileName: file.name,
      frameRecordId
    });
  };

  return {
    /**
     * Adopts a change set that another surface created (today: the Private
     * Agent materialising a research result) into this hook's existing review
     * lifecycle, so the pending banner, apply action and history all behave
     * exactly as they do for a locally created change set. Nothing is applied.
     */
    adoptPendingChangeSet: (response: GraphChangeSetCreateResponse, sourceLabel: string) => {
      setChangeSetState({ status: "pending", response, sourceLabel });
      changeSetHistoryMutation.mutate({ frameRecordId: response.changeSet.frameRecordId });
    },
    applyChangeSet: (variables: ChangeSetActionVariables) => {
      if (confirmApplyChangeSet(variables, copy)) {
        applyChangeSetMutation.mutate(variables);
      }
    },
    changeSetAuditExportState,
    changeSetAuditLogState,
    changeSetHistoryState,
    changeSetState,
    createChangeSetPending: createChangeSetMutation.isPending,
    createPendingChangeSet: (variables: Omit<CreateChangeSetVariables, "frameRecordId">) => {
      if (!frameRecordId) {
        setChangeSetState({
          status: "error",
          message: copy.errors.createNeedsCommittedFrame
        });
        return;
      }
      if (confirmCreateChangeSet(variables.sourceLabel, copy)) {
        createChangeSetMutation.mutate({ ...variables, frameRecordId });
      }
    },
    csvFileInputRef,
    csvImportPending: csvImportMutation.isPending,
    exportChangeSetAudit: (frameRecordId: string, changeSetId: string) =>
      changeSetAuditExportMutation.mutate({ changeSetId, frameRecordId }),
    handleCsvFileChange,
    handleCsvImportClick,
    inspectChangeSetAudit: (frameRecordId: string, changeSetId: string) =>
      changeSetAuditLogMutation.mutate({ changeSetId, frameRecordId }),
    refreshChangeSets: (frameRecordId: string) => changeSetHistoryMutation.mutate({ frameRecordId }),
    dismissChangeSetLifecycle: resetChangeSetLifecycle,
    resetChangeSetContext,
    resetChangeSetLifecycle,
    revertChangeSet: (variables: ChangeSetActionVariables) => {
      if (confirmRevertChangeSet(variables, copy)) {
        revertChangeSetMutation.mutate(variables);
      }
    }
  };
}

function commitFromChangeSetResponse(
  response: GraphChangeSetApplyResponse | GraphChangeSetRevertResponse
): GraphFrameCommitResponse {
  return {
    id: response.frame.id,
    status: response.frame.status,
    acceptedAt: response.frame.acceptedAt,
    nextAction: response.frame.nextAction,
    projectionStatus: response.projectionStatus
  };
}

function csvImportReason(fileName: string) {
  return `CSV import: ${fileName}`.slice(0, 500);
}

function csvImportClientMutationId(fileName: string) {
  const safeName = fileName.replace(/[^a-zA-Z0-9_.-]+/g, "-").slice(0, 72) || "file";
  return `csv-import:${safeName}:${Date.now()}`.slice(0, 128);
}

const StatusAutoClearMs = 8_000;

function shouldAutoClearChangeSetState(status: ChangeSetLifecycleState["status"]) {
  return status === "error" || status === "reverted";
}

function confirmCsvImport(fileName: string, copy: Dictionary["changeSet"]) {
  return confirmWorkspaceAction(copy.confirm.csvImport(fileName));
}

function confirmCreateChangeSet(sourceLabel: string, copy: Dictionary["changeSet"]) {
  return confirmWorkspaceAction(copy.confirm.create(sourceLabel));
}

function confirmApplyChangeSet(variables: ChangeSetActionVariables, copy: Dictionary["changeSet"]) {
  const summary = variables.importSummary
    ? copy.confirm.applyImportSummary(variables.importSummary.commandCount, variables.sourceLabel)
    : copy.confirm.applyChangeSetSummary(variables.changeSetId);
  return confirmWorkspaceAction(copy.confirm.apply(summary));
}

function confirmRevertChangeSet(variables: ChangeSetActionVariables, copy: Dictionary["changeSet"]) {
  return confirmWorkspaceAction(copy.confirm.revert(variables.changeSetId, variables.sourceLabel));
}

function confirmWorkspaceAction(message: string) {
  if (typeof window === "undefined") {
    return true;
  }

  return window.confirm(message);
}
